import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';
import {
    buildMatchedTopicsByPaper,
    hydrateCandidatesWithMatchedTopics,
    resolveActiveTopicCount,
    type PaperInboxApiCandidate,
} from './paperInboxApiAdapter';
import {
    resolvePaperInboxMatchedTopicsFallback,
    type PaperInboxFallbackMembershipQueryRow,
} from './paperInboxMatchedTopicsFallback';
import {
    kickPaperInboxReadinessSetup,
    type PaperInboxReadinessData,
} from './paperInboxReadinessFlow';
import { supabase, toError } from './supabase';
import type {
    EmailImportPaperRow,
    EmailImportRow,
} from './paperInboxViewModel';

const DEFAULT_BASE_URL = 'https://paperzilla.ai';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';
const PAPER_INBOX_READ_REQUEST_TIMEOUT_MS = 15000;
const PAPER_INBOX_READ_TIMEOUT_RETRY_DELAY_MS = 1000;
const PAPER_INBOX_READ_TIMEOUT_MAX_ATTEMPTS = 1;
const PAPER_INBOX_READINESS_MAX_POLL_ATTEMPTS = 8;
// i18n-deferred-start: technical-token -- internal transport errors; the UI maps these to descriptors.
const GENERIC_PAGE_LOAD_ERROR = 'Unable to load Paper Inbox right now. Please try again.';
const PAPER_INBOX_TIMEOUT_ERROR = 'Paper Inbox is taking longer than usual. Please try again.';
const PAPER_INBOX_NETWORK_ERROR = 'Could not reach the Paper Inbox endpoint.';
const PAPER_INBOX_SESSION_EXPIRED_ERROR = 'Your session expired. Please sign in again.';
const PAPER_INBOX_SIGN_IN_REQUIRED_ERROR = 'You need to sign in to load Paper Inbox.';
// i18n-deferred-end
const paperInboxProjectSummaryCache = new Map<string, Array<{ id: string; name: string }>>();

export interface PaperInboxPageData {
    imports: EmailImportRow[];
    candidates: EmailImportPaperRow[];
    activeTopicCount: number;
}

export interface PaperInboxSummaryPageData {
    imports: EmailImportRow[];
    activeTopicCount: number;
}

export interface PaperInboxImportDetailData {
    emailImport: EmailImportRow;
    candidates: EmailImportPaperRow[];
    activeTopicCount: number;
}

export const EMPTY_PAPER_INBOX_PAGE_DATA: PaperInboxPageData = {
    imports: [],
    candidates: [],
    activeTopicCount: 0,
};

export interface FetchPaperInboxPageOptions {
    page?: number;
    pageSize?: number;
    userId?: string | null;
    onSetupChange?: (isSettingUp: boolean) => void;
    onSetupReady?: () => void | Promise<void>;
}

export interface EnsurePaperInboxReadyOptions {
    onSetupChange?: (isSettingUp: boolean) => void;
    onSetupReady?: () => void | Promise<void>;
}

interface PaperInboxListResponse {
    page: number;
    page_size: number;
    imports: EmailImportRow[];
    candidates: PaperInboxApiCandidate[];
    active_topic_count?: number;
}

interface PaperInboxDetailResponse {
    email_import: EmailImportRow;
    candidates: PaperInboxApiCandidate[];
    active_topic_count?: number;
}

interface PaperInboxReadinessResponse {
    ready: boolean;
    reasons?: {
        identity_claims?: boolean;
        auth_gate_release?: boolean;
        project_mirroring?: boolean;
    };
    poll_after_ms?: number;
}

interface PaperInboxReadTimeoutReportContext {
    operation: 'email_imports_list' | 'email_import_detail' | 'email_import_readiness' | 'email_import_reconcile';
    page?: number;
    pageSize?: number;
    emailImportId?: number;
}

type PaperInboxFailureKind = 'api_read' | 'project_fallback' | 'project_paper_fallback';

function isExpectedPaperInboxError(error: Error): boolean {
    return error.message === PAPER_INBOX_SESSION_EXPIRED_ERROR
        || error.message === PAPER_INBOX_SIGN_IN_REQUIRED_ERROR;
}

function capturePaperInboxFailure(
    error: unknown,
    {
        kind,
        operation,
        page,
        pageSize,
        path,
        endpointCount,
        attemptCount,
        retryExhausted,
        extra = {},
    }: {
        kind: PaperInboxFailureKind;
        operation?: string;
        page?: number;
        pageSize?: number;
        path?: string;
        endpointCount?: number;
        attemptCount?: number;
        retryExhausted?: 'timeout' | 'network' | null;
        extra?: Record<string, unknown>;
    }
): void {
    const normalizedError = toError(error);

    if (isExpectedPaperInboxError(normalizedError)) {
        return;
    }

    Sentry.captureException(normalizedError, {
        fingerprint: ['paper-inbox', kind, operation || 'unknown'],
        tags: {
            surface: 'dashboard_inbox',
            kind,
            operation: operation || 'unknown',
            platform: Platform.OS,
            retry_exhausted: retryExhausted || 'none',
        },
        extra: {
            path: path ?? null,
            page: page ?? null,
            page_size: pageSize ?? null,
            endpoint_count: endpointCount ?? null,
            attempt_count: attemptCount ?? null,
            ...extra,
        },
    });
}

function nowMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now();
    }

    return Date.now();
}

function queryRowCount(value: unknown): number {
    return Array.isArray(value) ? value.length : (value ? 1 : 0);
}

function delayMs(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

async function timedQuery<T extends { data?: unknown; error?: { message?: string | null } | null }>(
    label: string,
    fn: () => PromiseLike<T>,
    metadata: Record<string, unknown> = {}
): Promise<T> {
    const startedAt = nowMs();
    const result = await fn();
    const durationMs = Math.round(nowMs() - startedAt);

    console.log(`[PaperInbox] ${label} ${durationMs}ms`, {
        ...metadata,
        rows: queryRowCount(result?.data),
        error: result?.error?.message ?? null,
    });

    return result;
}

function needsMatchedTopicsFallback(candidates: PaperInboxApiCandidate[]): boolean {
    return candidates.some((candidate) => (
        candidate.paper_id && candidate.matched_topics === undefined
    ));
}

async function fetchPaperInboxProjectSummaries(userId: string): Promise<Array<{ id: string; name: string }>> {
    const cached = paperInboxProjectSummaryCache.get(userId);
    if (cached) {
        return cached;
    }

    const { data: projectData, error: userProjectsError } = await timedQuery<{
        data: Array<{ id: string; name: string }> | null;
        error: { message?: string | null } | null;
    }>(
        'paper_inbox_projects',
        () => supabase
            .from('project')
            .select('id, name')
            .eq('user_id', userId)
            .eq('is_inbox', false)
            .is('deleted_at', null)
    );

    if (userProjectsError) {
        const normalizedError = toError(userProjectsError);
        capturePaperInboxFailure(normalizedError, {
            kind: 'project_fallback',
            operation: 'project_fallback',
        });
        throw normalizedError;
    }

    const projects = projectData || [];
    paperInboxProjectSummaryCache.set(userId, projects);
    return projects;
}

function trimTrailingSlash(url: string): string {
    return url.replace(/\/+$/, '');
}

function getConfiguredBaseUrl(): string {
    return trimTrailingSlash(process.env.EXPO_PUBLIC_BASE_URL || DEFAULT_BASE_URL);
}

function isLocalWebDevHost(): boolean {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
}

function isSameOriginOnWeb(url: string): boolean {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    try {
        return new URL(url).origin === window.location.origin;
    } catch {
        return false;
    }
}

function getPaperInboxEndpointCandidates(path: string): string[] {
    const candidates: string[] = [];
    const configuredEndpoint = `${getConfiguredBaseUrl()}${path}`;
    const localEndpoint = `${DEFAULT_LOCAL_API_BASE_URL}${path}`;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const sameOriginConfigured = isSameOriginOnWeb(configuredEndpoint);

        if (isLocalWebDevHost()) {
            if (sameOriginConfigured) {
                candidates.push(path);
            }
            candidates.push(localEndpoint);
            if (!sameOriginConfigured && !candidates.includes(configuredEndpoint)) {
                candidates.push(configuredEndpoint);
            }
            return candidates;
        }

        candidates.push(path);
        if (!sameOriginConfigured && !candidates.includes(configuredEndpoint)) {
            candidates.push(configuredEndpoint);
        }
        return candidates;
    }

    candidates.push(configuredEndpoint);
    return candidates;
}

async function parseErrorResponse(response: Response): Promise<string> {
    // i18n-ignore: technical-token -- backend response parsing; never rendered directly.
    let message = `Request failed (${response.status})`;

    try {
        const body = await response.json();
        if (body?.detail) {
            if (typeof body.detail === 'object' && body.detail?.message) {
                message = String(body.detail.message);
            } else {
                message = String(body.detail);
            }
        } else if (body?.message) {
            message = String(body.message);
        } else if (body?.error) {
            message = String(body.error);
        }
    } catch {
        try {
            const text = await response.text();
            if (text) {
                message = text;
            }
        } catch {
            // Keep default message.
        }
    }

    if (response.status === 401) {
        // i18n-ignore: technical-token -- transport error code; views render localized copy.
        return 'AUTH_SESSION_EXPIRED';
    }

    return message;
}

async function getSessionAccessToken(
    missingSessionMessage = 'You need to sign in to load Paper Inbox.'
): Promise<string> {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
        throw new Error(error.message || 'Failed to get current session.');
    }

    if (!session?.access_token) {
        throw new Error(missingSessionMessage);
    }

    return session.access_token;
}

async function requestPaperInboxListPage(
    page: number,
    pageSize: number,
    accessToken: string,
    includeCandidates = true,
): Promise<PaperInboxListResponse> {
    const query = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
    });
    if (!includeCandidates) {
        query.set('include_candidates', 'false');
    }
    return requestPaperInboxRead<PaperInboxListResponse>({
        path: `/api/email-imports?${query.toString()}`,
        accessToken,
        genericError: GENERIC_PAGE_LOAD_ERROR,
        timeoutReportContext: {
            operation: 'email_imports_list',
            page,
            pageSize,
        },
    });
}

async function requestPaperInboxImportDetail(
    emailImportId: number,
    accessToken: string
): Promise<PaperInboxDetailResponse> {
    return requestPaperInboxRead<PaperInboxDetailResponse>({
        path: `/api/email-imports/${encodeURIComponent(String(emailImportId))}`,
        accessToken,
        genericError: GENERIC_PAGE_LOAD_ERROR,
        timeoutReportContext: {
            operation: 'email_import_detail',
            emailImportId,
        },
    });
}

async function requestPaperInboxReadiness(accessToken: string): Promise<PaperInboxReadinessResponse> {
    return requestPaperInboxRead<PaperInboxReadinessResponse>({
        path: '/api/email-imports/readiness',
        accessToken,
        genericError: GENERIC_PAGE_LOAD_ERROR,
        timeoutReportContext: {
            operation: 'email_import_readiness',
        },
    });
}

async function requestPaperInboxReconcile(accessToken: string): Promise<PaperInboxReadinessResponse> {
    return requestPaperInboxRead<PaperInboxReadinessResponse>({
        method: 'POST',
        path: '/api/email-imports/reconcile',
        accessToken,
        genericError: GENERIC_PAGE_LOAD_ERROR,
        timeoutReportContext: {
            operation: 'email_import_reconcile',
        },
    });
}

async function requestPaperInboxRead<T>({
    method = 'GET',
    path,
    accessToken,
    genericError,
    timeoutReportContext,
}: {
    method?: 'GET' | 'POST';
    path: string;
    accessToken: string;
    genericError: string;
    timeoutReportContext: PaperInboxReadTimeoutReportContext;
}): Promise<T> {
    const endpoints = getPaperInboxEndpointCandidates(path);
    let lastError: Error | null = null;
    let totalAttempts = 0;
    let didExhaustTimeoutRetries = false;
    let didExhaustNetworkRetries = false;
    let shouldStopAllCandidates = false;

    for (let index = 0; index < endpoints.length; index += 1) {
        const endpoint = endpoints[index];
        for (let attempt = 1; attempt <= PAPER_INBOX_READ_TIMEOUT_MAX_ATTEMPTS; attempt += 1) {
            totalAttempts += 1;
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const timeoutId = controller
                ? setTimeout(() => controller.abort(), PAPER_INBOX_READ_REQUEST_TIMEOUT_MS)
                : null;

            try {
                const response = await fetch(endpoint, {
                    method,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    ...(controller ? { signal: controller.signal } : {}),
                });

                if (!response.ok) {
                    const message = await parseErrorResponse(response);
                    const hasMoreCandidates = index < endpoints.length - 1;
                    const shouldRetry = hasMoreCandidates && (response.status === 404 || response.status === 405);

                    if (shouldRetry) {
                        lastError = new Error(message);
                        break;
                    }

                    throw new Error(message);
                }

                return await response.json() as T;
            } catch (error: any) {
                if (error?.name === 'AbortError') {
                    lastError = new Error(PAPER_INBOX_TIMEOUT_ERROR);
                    const shouldRetryTimeout = attempt < PAPER_INBOX_READ_TIMEOUT_MAX_ATTEMPTS;
                    if (shouldRetryTimeout) {
                        await delayMs(PAPER_INBOX_READ_TIMEOUT_RETRY_DELAY_MS);
                        continue;
                    }

                    didExhaustTimeoutRetries = true;
                    continue;
                }

                if (error instanceof TypeError) {
                    lastError = new Error(PAPER_INBOX_NETWORK_ERROR);
                    const shouldRetryNetwork = attempt < PAPER_INBOX_READ_TIMEOUT_MAX_ATTEMPTS;
                    if (shouldRetryNetwork) {
                        await delayMs(PAPER_INBOX_READ_TIMEOUT_RETRY_DELAY_MS);
                        continue;
                    }

                    didExhaustNetworkRetries = true;
                    continue;
                }

                lastError = error instanceof Error ? error : new Error(genericError);
                shouldStopAllCandidates = true;
                break;
            } finally {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            }
        }

        if (shouldStopAllCandidates) {
            break;
        }
    }

    const finalError = lastError || new Error(genericError);
    const retryExhausted =
        didExhaustTimeoutRetries && finalError.message === PAPER_INBOX_TIMEOUT_ERROR
            ? 'timeout'
            : didExhaustNetworkRetries && finalError.message === PAPER_INBOX_NETWORK_ERROR
                ? 'network'
                : null;

    capturePaperInboxFailure(finalError, {
        kind: 'api_read',
        operation: timeoutReportContext.operation,
        path,
        page: timeoutReportContext.page,
        pageSize: timeoutReportContext.pageSize,
        endpointCount: endpoints.length,
        attemptCount: totalAttempts,
        retryExhausted,
        extra: {
            timeout_ms: PAPER_INBOX_READ_REQUEST_TIMEOUT_MS,
            has_alternate_endpoint: endpoints.length > 1,
        },
    });

    throw finalError;
}

function normalizePaperInboxReadiness(response: PaperInboxReadinessResponse): PaperInboxReadinessData {
    const reasons = response.reasons ?? {};

    return {
        ready: response.ready === true,
        reasons: {
            identityClaims: reasons.identity_claims === true,
            authGateRelease: reasons.auth_gate_release === true,
            projectMirroring: reasons.project_mirroring === true,
        },
        pollAfterMs: typeof response.poll_after_ms === 'number' && response.poll_after_ms > 0
            ? response.poll_after_ms
            : 1000,
    };
}

function reportPaperInboxReadinessPollExhausted(readiness: PaperInboxReadinessData): void {
    Sentry.captureMessage('Paper Inbox readiness polling exceeded bounded attempts', {
        level: 'warning',
        fingerprint: ['paper-inbox', 'readiness-poll-exhausted'],
        tags: {
            surface: 'dashboard_inbox',
            kind: 'api_read',
            operation: 'email_import_readiness',
            platform: Platform.OS,
        },
        extra: {
            max_poll_attempts: PAPER_INBOX_READINESS_MAX_POLL_ATTEMPTS,
            poll_after_ms: readiness.pollAfterMs,
            reasons: readiness.reasons,
        },
    });
}

function reportPaperInboxReadinessSetupError(error: unknown): void {
    const normalizedError = error instanceof Error ? error : new Error('Paper Inbox setup check failed.');
    if (isExpectedPaperInboxError(normalizedError)) {
        return;
    }

    Sentry.captureException(normalizedError, {
        tags: {
            surface: 'dashboard_inbox',
            kind: 'api_read',
            operation: 'email_import_readiness_setup',
            platform: Platform.OS,
        },
    });
}

export async function fetchPaperInboxReadiness(): Promise<PaperInboxReadinessData> {
    const accessToken = await getSessionAccessToken('You need to sign in to load Paper Inbox.');
    const response = await requestPaperInboxReadiness(accessToken);
    return normalizePaperInboxReadiness(response);
}

export async function reconcilePaperInbox(): Promise<PaperInboxReadinessData> {
    const accessToken = await getSessionAccessToken('You need to sign in to load Paper Inbox.');
    const response = await requestPaperInboxReconcile(accessToken);
    return normalizePaperInboxReadiness(response);
}

export async function ensurePaperInboxReady({
    onSetupChange,
    onSetupReady,
}: EnsurePaperInboxReadyOptions = {}): Promise<void> {
    void kickPaperInboxReadinessSetup({
        checkReadiness: fetchPaperInboxReadiness,
        reconcile: reconcilePaperInbox,
        delay: delayMs,
        onSetupChange,
        onSetupReady,
        onSetupError: reportPaperInboxReadinessSetupError,
        onPollExhausted: reportPaperInboxReadinessPollExhausted,
        maxPollAttempts: PAPER_INBOX_READINESS_MAX_POLL_ATTEMPTS,
    });
}

function shouldKickPaperInboxReadinessSetup(pageData: PaperInboxPageData): boolean {
    if (pageData.imports.length === 0) {
        return true;
    }

    return pageData.imports.some((row) => {
        const parseState = String(row.parse_state || '').toLowerCase();
        const resolutionState = String(row.resolution_state || '').toLowerCase();
        return (
            parseState === 'queued'
            || parseState === 'parsing'
            || resolutionState === 'queued'
            || resolutionState === 'resolving'
        );
    });
}

export async function fetchPaperInboxPageDataAfterReadiness({
    onSetupChange,
    onSetupReady,
    ...pageOptions
}: FetchPaperInboxPageOptions = {}): Promise<PaperInboxPageData> {
    const pageData = await fetchPaperInboxPageData(pageOptions);

    if (shouldKickPaperInboxReadinessSetup(pageData)) {
        void kickPaperInboxReadinessSetup({
            checkReadiness: fetchPaperInboxReadiness,
            reconcile: reconcilePaperInbox,
            delay: delayMs,
            onSetupChange,
            onSetupReady,
            onSetupError: reportPaperInboxReadinessSetupError,
            onPollExhausted: reportPaperInboxReadinessPollExhausted,
            maxPollAttempts: PAPER_INBOX_READINESS_MAX_POLL_ATTEMPTS,
        });
    }

    return pageData;
}

function dedupeById<T extends { id: string | number }>(rows: T[]): T[] {
    const seen = new Set<string | number>();

    return rows.filter((row) => {
        if (seen.has(row.id)) {
            return false;
        }

        seen.add(row.id);
        return true;
    });
}

async function hydratePaperInboxCandidates({
    candidates,
    userProjects,
    page,
    pageSize,
}: {
    candidates: PaperInboxApiCandidate[];
    userProjects: Array<{ id: string; name: string }>;
    page?: number;
    pageSize?: number;
}): Promise<EmailImportPaperRow[]> {
    if (!needsMatchedTopicsFallback(candidates) || userProjects.length === 0) {
        return hydrateCandidatesWithMatchedTopics(candidates, new Map());
    }

    const paperIds = [...new Set(
        candidates.map((row) => row.paper_id).filter(Boolean)
    )];
    const projectIds = userProjects.map((project) => project.id);

    const fallbackTopicRowsResult = paperIds.length === 0 || projectIds.length === 0
        ? { data: [] as PaperInboxFallbackMembershipQueryRow[], error: null }
        : await timedQuery<{
            data: PaperInboxFallbackMembershipQueryRow[] | null;
            error: { message?: string | null } | null;
        }>(
            'project_paper_saved_memberships',
            () => supabase
                .from('project_paper')
                .select(`
                    id,
                    paper_id,
                    project_id
                `)
                .in('project_id', projectIds)
                .in('paper_id', paperIds)
                .order('id', { ascending: false }),
            {
                projectCount: projectIds.length,
                paperCount: paperIds.length,
            }
        );

    const resolvedFallback = resolvePaperInboxMatchedTopicsFallback({
        data: fallbackTopicRowsResult.data,
        error: fallbackTopicRowsResult.error,
        userProjects,
        normalizeError: toError,
    });

    if (resolvedFallback.error) {
        capturePaperInboxFailure(resolvedFallback.error, {
            kind: 'project_paper_fallback',
            operation: 'project_paper_fallback',
            page,
            pageSize,
            extra: {
                project_count: projectIds.length,
                paper_count: paperIds.length,
                degraded: true,
            },
        });
        console.warn('[PaperInbox] project_paper fallback failed; continuing without matched topics', {
            page,
            pageSize,
            projectCount: projectIds.length,
            paperCount: paperIds.length,
            message: resolvedFallback.error.message,
        });
    }

    const matchedTopicsByPaper = buildMatchedTopicsByPaper(resolvedFallback.rows);
    return hydrateCandidatesWithMatchedTopics(candidates, matchedTopicsByPaper);
}

export function mergePaperInboxPageData(
    current: PaperInboxPageData,
    incoming: PaperInboxPageData
): PaperInboxPageData {
    return {
        imports: dedupeById([...current.imports, ...incoming.imports]),
        candidates: dedupeById([...current.candidates, ...incoming.candidates]),
        activeTopicCount: incoming.activeTopicCount,
    };
}

async function resolvePaperInboxActiveTopicCount(
    userId: string,
    apiActiveTopicCount?: number,
    requireUserProjects = false,
): Promise<{
    activeTopicCount: number;
    userProjects: Array<{ id: string; name: string }>;
}> {
    if (apiActiveTopicCount !== undefined && !requireUserProjects) {
        return {
            activeTopicCount: apiActiveTopicCount,
            userProjects: [],
        };
    }

    const userProjects = await fetchPaperInboxProjectSummaries(userId);
    return {
        activeTopicCount: resolveActiveTopicCount(apiActiveTopicCount, userProjects.length),
        userProjects,
    };
}

export async function fetchPaperInboxSummaryPageData({
    page = 0,
    pageSize = 25,
    userId = null,
}: FetchPaperInboxPageOptions = {}): Promise<PaperInboxSummaryPageData> {
    if (!userId) {
        return {
            imports: [],
            activeTopicCount: 0,
        };
    }

    const accessToken = await getSessionAccessToken('You need to sign in to load Paper Inbox.');
    const pageResponse = await requestPaperInboxListPage(page, pageSize, accessToken, false);
    const { activeTopicCount } = await resolvePaperInboxActiveTopicCount(
        userId,
        pageResponse.active_topic_count
    );

    return {
        imports: pageResponse.imports || [],
        activeTopicCount,
    };
}

export async function fetchPaperInboxImportDetail({
    emailImportId,
    userId,
}: {
    emailImportId: number;
    userId: string | null;
}): Promise<PaperInboxImportDetailData> {
    if (!userId) {
        throw new Error(PAPER_INBOX_SIGN_IN_REQUIRED_ERROR);
    }

    const accessToken = await getSessionAccessToken('You need to sign in to load Paper Inbox.');
    const detailResponse = await requestPaperInboxImportDetail(emailImportId, accessToken);
    const candidates = detailResponse.candidates || [];
    const { activeTopicCount, userProjects } = await resolvePaperInboxActiveTopicCount(
        userId,
        detailResponse.active_topic_count,
        needsMatchedTopicsFallback(candidates)
    );
    const hydratedCandidates = await hydratePaperInboxCandidates({
        candidates,
        userProjects,
    });

    return {
        emailImport: detailResponse.email_import,
        candidates: hydratedCandidates,
        activeTopicCount,
    };
}

export async function fetchPaperInboxPageData({
    page = 0,
    pageSize = 25,
    userId = null,
}: FetchPaperInboxPageOptions = {}): Promise<PaperInboxPageData> {
    const fetchStartedAt = nowMs();

    if (!userId) {
        return EMPTY_PAPER_INBOX_PAGE_DATA;
    }

    const accessToken = await getSessionAccessToken('You need to sign in to load Paper Inbox.');
    const pageResponse = await requestPaperInboxListPage(page, pageSize, accessToken, true);
    const imports = pageResponse.imports || [];
    const candidates = pageResponse.candidates || [];
    const { activeTopicCount, userProjects } = await resolvePaperInboxActiveTopicCount(
        userId,
        pageResponse.active_topic_count,
        needsMatchedTopicsFallback(candidates)
    );

    if (imports.length === 0) {
        return {
            ...EMPTY_PAPER_INBOX_PAGE_DATA,
            activeTopicCount,
        };
    }

    const hydratedCandidates = await hydratePaperInboxCandidates({
        candidates,
        userProjects,
        page,
        pageSize,
    });

    const pageData = {
        imports: imports as EmailImportRow[],
        candidates: hydratedCandidates,
        activeTopicCount,
    };

    console.log('[PaperInbox] fetchPaperInboxPageData total', {
        page,
        pageSize,
        durationMs: Math.round(nowMs() - fetchStartedAt),
        imports: pageData.imports.length,
        candidates: pageData.candidates.length,
        activeTopicCount: pageData.activeTopicCount,
        usedActiveTopicFallback: pageResponse.active_topic_count === undefined,
        usedMatchedTopicsFallback: needsMatchedTopicsFallback(candidates),
    });

    return pageData;
}
